/**
 * Public layout component for shared content pages
 * This layout is used for publicly accessible pages that display shared content
 * It includes a header with logo and copy link button, content area, and footer
 */
import React from 'react';
import styled from 'styled-components';
import logo from '../../../assets/images/logoHeader.png';
import { LuCopy } from 'react-icons/lu';
import { toast } from 'sonner';
import { tr } from '../../../translation';

// Styled components for layout
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background: ${({ theme }) => theme.homeBackground};
  padding: ${({ theme }) => (!theme.isMobile ? '0 10%' : '0')};
`;

const Header = styled.header`
  width: 100%;
  display: flex;
  padding: 15px ${({ theme }) => (theme.isMobile ? '3%' : '10%')};
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.homeBackground};
  border-bottom: 1px solid ${({ theme }) => theme.text.optionIcon}20;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
`;

const Logo = styled.img`
  height: 40px;
`;

const CopyButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.text.optionIcon};
  border: 1px solid ${({ theme }) => theme.text.optionIcon};
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  transition: 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  font-weight: 500;

  &:hover {
    background-color: ${({ theme }) => theme.text.light}10 !important;
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: 80px;
  padding-bottom: 26px;
`;

const Footer = styled.footer`
  width: 100%;
  text-align: center;
  color: ${({ theme }) => theme.text.optionIcon};
  padding: 10px;
  font-size: 14px;
  background: ${({ theme }) => theme.homeBackground};
  position: fixed;
  bottom: 0;
  left: 0;
`;

/**
 * Props interface for the PublicLayout component
 */
interface PublicLayoutProps {
  children: React.ReactNode;
}

/**
 * PublicLayout component provides consistent layout for public/shared pages
 * @param children - React components to render within the layout
 */
const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  /**
   * Copies the current URL to clipboard and shows a success toast
   */
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success(tr('chatPage.codeCopied'));
  };

  return (
    <Container>
      <Header>
        <Logo src={logo} alt="Genio Logo" />
        <CopyButton onClick={handleCopyLink}>
          <LuCopy /> Copy Link
        </CopyButton>
      </Header>
      <Content>{children}</Content>
      <Footer>This page was shared with you.</Footer>
    </Container>
  );
};

export default PublicLayout;
